import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, frames } from "../../../Styles";
import { LocationInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { ProgressBar, SecondaryPopover } from "../..";
import { useAttentionLog } from "../../../Services";
import { BarTypes } from "../../ProgressViewer/_ProgressViewer/progressDefinitions";
import { currentLfViewTypes, useLfContext } from "../_LocationFeeder/lfPenContext";
import { Animal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import { useAnimalConsumptionById } from "../../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";
import { useSingleConsumption } from "../../_Components/consumption";

interface PenBasic_Props {
  pen: LocationInfo;
  gridArea: string;
  focus_view?: boolean;
  popoverContent?: JSX.Element;
  overrides?: {
    primaryColor?: colors;
    textColor?: colors;
    width?: string;
    border?: string;
    attention?: number
  }
}

export default function LF_PEN({ pen, gridArea, focus_view, popoverContent, overrides }: PenBasic_Props) {
  const { t } = useTranslation();
  const { state: { selectedPens, currentPenViewType, penContent, connection, domain, systemId }, dispatch } = useLfContext();
  
  // Styles
  const _primaryColor = overrides && overrides.primaryColor ? overrides.primaryColor : colors.greyLightSecondary
  const _textColor = overrides && overrides.textColor ? overrides.textColor : colors.greyDarkPrimary
  const _width = overrides && overrides.width ? overrides.width : "80px"
  const _border = overrides && overrides.border ? overrides.border : "none"

  // #region | Attentions
  const { data: attentionLogState, call: getAttentionLogs } = useAttentionLog(connection);

  useEffect(() => {
    if (systemId) {
      getAttentionLogs({ ids: [systemId] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const logData = useMemo(() => {
    if (domain in attentionLogState && attentionLogState[domain] && typeof attentionLogState[domain] === "object" && "data" in attentionLogState[domain] && attentionLogState[domain].data && systemId) {
      return attentionLogState[domain].data.filter((log) => log.fk_systems === systemId && log.dismissed !== true);
    }
    return []
  }, [attentionLogState, domain, systemId])

  const hasAttention = useMemo(() => 
    overrides&&overrides.attention ? true : logData.some(data => data.fk_locations === pen.location_id), [logData, overrides, pen.location_id]);
  
  const penAttentionLevel = useMemo(() => overrides&&overrides.attention ? overrides.attention : logData.find(data => data.fk_locations === pen.location_id)?.level, [logData, overrides, pen.location_id])
  
  const attentionOverlay = <Attention attentionLevel={penAttentionLevel} hasAttention={hasAttention}/>
  // #endregion
  
  // #region | SystemControle / On pen click
  const handleClick = () => {
    const alreadySelected = selectedPens?.some((p) => p === pen);

    let newSelectedList;

    if (alreadySelected) {
      newSelectedList = selectedPens?.filter((p) => p !== pen);
    } else {
      newSelectedList = [...(selectedPens ?? []), pen];
    }

    dispatch({ selectedPens: newSelectedList });
  }
  // #endregion

  // #region | Pen Content
  const thisAnimal = useMemo(() => {
    if (!pen) return null;
    return pen.animals.find(a => a.location_id === pen.location_id)
  }, [pen]) 

  const _penContent = useMemo(() => penContent === 0
    ? thisAnimal
      ? `${thisAnimal.animal_number}`
      : t("locationFeederPages.sections.noAnimal", {defaultValue: "No Animal"})
  : penContent === 1
    ? pen
      ? `${pen.name}`
      : t("noData", {defaultValue: "No Data"})
      : undefined, [pen, penContent, t, thisAnimal]);
  
  const penContentRes = useMemo(() => (!thisAnimal && penContent === 0) || (!penContent && penContent === 1)
    ? <p style={{ ...css.emptyPen, opacity: "0.5" }}>
        {_penContent}
      </p>
    : <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>{_penContent}</p>, [_penContent, penContent, thisAnimal])
  // #endregion

  const Pen = () => {
    switch (currentPenViewType) {
      case currentLfViewTypes.systemControle:
        return (
          <frames.LfPen onClick={handleClick} $gridArea={gridArea} $selectable $isSelected={selectedPens && selectedPens.some(p => p === pen)} $primaryColor={_primaryColor} $textColor={_textColor} $width={_width} $border={_border} style={{ ...fonts.paragraph, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4), cursor: "pointer" }} >
            { popoverContent ?
              <SecondaryPopover
                content={<div style={{}}>{pen?.name} {overrides&&overrides.attention ? attentionOverlay : undefined}</div>}
                popoverHeader=""
                popoverContent={<>{popoverContent}</>}
                overrides={{ primaryColor: colors.greyDarkPrimary }}
              />
            : pen?.name }      
          </frames.LfPen>
        );
      case currentLfViewTypes.attention:
        return (
          <frames.LfPen $primaryColor={_primaryColor} $textColor={_textColor} $gridArea={gridArea} $width={_width} $border={_border} >
            { penContentRes }
            { hasAttention ? attentionOverlay : undefined }
          </frames.LfPen>
        );
      case currentLfViewTypes.remainingToday:
      case currentLfViewTypes.remainingYesterday:
        return ( thisAnimal
          ? <div style={{gridArea: gridArea}}><Consumption pen={pen} thisAnimal={thisAnimal} currentPenViewType={currentPenViewType} penContent={penContent}/></div>
          : <frames.LfPen $primaryColor={_primaryColor} $textColor={_textColor} $gridArea={gridArea} $width={_width} $border={_border} >
              { penContentRes }
            </frames.LfPen>
          );
      case currentLfViewTypes.basic:
      default:
        return (
          <frames.LfPen $primaryColor={_primaryColor} $textColor={_textColor} $gridArea={gridArea} $width={_width} $border={_border} >
            { penContentRes }
          </frames.LfPen>
        )
    }
  }

  return Pen();
}

// #region | Attention Pen
const Attention = memo(({ attentionLevel, hasAttention }: { attentionLevel?: number, hasAttention: boolean }) => {
  const [animate, setAnimate] = useState<boolean>(false);
  const _width = animate ? "10px" : "0px"
  
  useEffect(() => {
    setAnimate(true);
  }, []);

  if (!hasAttention) return null;
  return (
    <div style={{
      top: "0",
      left: "0",
      position: "absolute",
      zIndex: "2",
      height: "100%",
      width: _width,
      backgroundColor: attentionLevel === 0 ? colors.systemRed : attentionLevel === 5 ? colors.graphBlueTwo : colors.systemYellow,
      transition: "width 0.5s ease"
    }} />
  ) 
})
// #endregion

// #region | Consumption Pens
function Consumption({ pen, thisAnimal, penContent, currentPenViewType }: { pen: LocationInfo, thisAnimal: Animal, penContent?: number, currentPenViewType: currentLfViewTypes }) {
  const animalConsumption = useSingleConsumption(thisAnimal?.animal_id);
  
  if (!thisAnimal) return;
  
  return (
    <ProgressBar
      barType={BarTypes.pen}
      animation
      labels={{
        penLabel: penContent === 0 ? thisAnimal.animal_number : pen.name,
        noDataDisclaimer: penContent === 0 ? `${thisAnimal.animal_number}` : `${pen.name}`
      }}
      total={currentPenViewType === currentLfViewTypes.remainingYesterday ? animalConsumption[1]?.feed.total : animalConsumption[0]?.feed.total}
      progressed={currentPenViewType === currentLfViewTypes.remainingYesterday ? animalConsumption[1]?.feed.remaning : animalConsumption[0]?.feed.remaning}
    />
  )
};
// #endregion